import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/config';
import Button from '../common/Button';
import { PermissionGate } from '../common/PermissionGate';

interface CustomerPortalUser {
  id: string;
  customerId: string;
  email: string;
  name: string | null;
  status: 'active' | 'inactive';
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PortalAccessSectionProps {
  customerId: string;
  customerEmail?: string;
  customerName?: string;
}

export const PortalAccessSection: React.FC<PortalAccessSectionProps> = ({
  customerId,
  customerEmail,
  customerName,
}) => {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [email, setEmail] = useState(customerEmail || '');
  const [name, setName] = useState(customerName || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch existing portal user for this customer
  const { data: portalUser, isLoading } = useQuery({
    queryKey: ['customerPortalUser', customerId],
    queryFn: async () => {
      const response = await api.get<CustomerPortalUser | null>(
        `/api/v1/admin/customer-users/by-customer/${customerId}`
      );
      return response;
    },
  });

  // Create portal user mutation
  const createMutation = useMutation({
    mutationFn: (data: { customerId: string; email: string; name?: string }) =>
      api.post<CustomerPortalUser>('/api/v1/admin/customer-users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerPortalUser', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setIsCreating(false);
      setSuccess('Portal account created. Welcome email sent with temporary password.');
      setTimeout(() => setSuccess(''), 5000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ tempPassword?: string }>(`/api/v1/admin/customer-users/${userId}/reset-password`, {}),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customerPortalUser', customerId] });
      if (data.tempPassword) {
        setSuccess(`Password reset. Temporary password: ${data.tempPassword}`);
      } else {
        setSuccess('Password reset. Email sent with new temporary password.');
      }
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: 'active' | 'inactive' }) =>
      api.patch<CustomerPortalUser>(`/api/v1/admin/customer-users/${userId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerPortalUser', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setSuccess('Account status updated.');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Delete portal user mutation
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/api/v1/admin/customer-users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerPortalUser', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setSuccess('Portal account deleted.');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  const handleCreateAccount = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    createMutation.mutate({
      customerId,
      email,
      name: name || undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Portal Access</h4>
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h4 className="text-sm font-medium text-gray-900 mb-3">Portal Access</h4>

      {/* Messages */}
      {error && (
        <div className="mb-3 p-2 text-sm text-red-700 bg-red-100 rounded">{error}</div>
      )}
      {success && (
        <div className="mb-3 p-2 text-sm text-green-700 bg-green-100 rounded font-mono">
          {success}
        </div>
      )}

      {portalUser ? (
        // Existing portal account
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-md p-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Email:</span>
                <span className="ml-2 text-gray-900">{portalUser.email}</span>
              </div>
              <div>
                <span className="text-gray-500">Name:</span>
                <span className="ml-2 text-gray-900">{portalUser.name || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <span
                  className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    portalUser.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {portalUser.status}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Last login:</span>
                <span className="ml-2 text-gray-900">
                  {portalUser.lastLoginAt
                    ? new Date(portalUser.lastLoginAt).toLocaleDateString()
                    : 'Never'}
                </span>
              </div>
            </div>
            {portalUser.mustChangePassword && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  Must change password on next login
                </span>
              </div>
            )}
          </div>

          <PermissionGate resource="customer_users" action="update">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  resetPasswordMutation.mutate(portalUser.id);
                }}
                disabled={resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
              </Button>

              {portalUser.status === 'active' ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateStatusMutation.mutate({ userId: portalUser.id, status: 'inactive' });
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateStatusMutation.mutate({ userId: portalUser.id, status: 'active' });
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Reactivate
                </Button>
              )}

              <PermissionGate resource="customer_users" action="delete">
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (confirm('Are you sure you want to delete this portal account?')) {
                      deleteMutation.mutate(portalUser.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  Delete Account
                </Button>
              </PermissionGate>
            </div>
          </PermissionGate>
        </div>
      ) : isCreating ? (
        // Create account form
        <form onSubmit={handleCreateAccount} className="space-y-3">
          <div>
            <label htmlFor="portal-email" className="block text-sm font-medium text-gray-700">
              Portal Email *
            </label>
            <input
              type="email"
              id="portal-email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="customer@example.com"
            />
          </div>
          <div>
            <label htmlFor="portal-name" className="block text-sm font-medium text-gray-700">
              Contact Name
            </label>
            <input
              type="text"
              id="portal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <p className="text-xs text-gray-500">
            A temporary password will be generated and sent to the email address.
          </p>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={createMutation.isPending}
              onClick={(e) => e.stopPropagation()}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Account'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsCreating(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        // No account - show create button
        <div>
          <p className="text-sm text-gray-500 mb-3">
            This customer does not have a portal account yet.
          </p>
          <PermissionGate resource="customer_users" action="create">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEmail(customerEmail || '');
                setName(customerName || '');
                setIsCreating(true);
              }}
            >
              Create Portal Account
            </Button>
          </PermissionGate>
        </div>
      )}
    </div>
  );
};
